import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ComponentCard from '../../components/common/ComponentCard';
import SearchableSelect, {
  type SearchableSelectOption,
} from '../../components/common/SearchableSelect';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import { Modal } from '../../components/ui/modal';
import { useAuth } from '../../context/AuthContext';
import { CloseIcon } from '../../icons';
import {
  api,
  apiEndpoints,
  type WebsiteRecrawlJobResponse,
} from '../../utils/apiService';
import {
  ACCEPTED_FILE_COPY,
  ACCEPTED_FILE_TYPES,
  formatBytes,
  validateUploadFiles,
  validateUploadFilesBasic,
} from '../../utils/fileUploadValidation';
import {
  type TableSortState,
  toOrderingParam,
  toggleSortState,
} from '../../utils/listOrdering';
import WebsiteKnowledgeImportModal from './WebsiteKnowledgeImportModal';
import {
  boundedPollBackoff,
  isWebsiteOperationTerminal,
  KnowledgePollController,
} from './knowledgeBasePolling';
import {
  categoryOptions,
  labelFor,
  scopeOptions,
  type ExtractionStatus,
  type KnowledgeCategory,
  type KnowledgeScope,
  type KnowledgeSourceFile,
  type KnowledgeStatus,
} from './knowledgeBaseShared';

type KnowledgeSourceListResponse = {
  results: KnowledgeSourceFile[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
};

type CompilationJobStatus =
  | 'pending'
  | 'collecting'
  | 'extracting'
  | 'synthesizing'
  | 'validating'
  | 'completed'
  | 'failed';

type CompilationJob = {
  id: number;
  store: number;
  requested_by: number | null;
  status: CompilationJobStatus;
  source_file_ids: number[];
  model_name: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  error_message: string;
  metadata: Record<string, unknown>;
  artifact_id: number | null;
  created_at: string;
};

type Artifact = {
  id: number;
  store: number;
  job_id: number;
  status: 'draft' | 'needs_review' | 'approved' | 'superseded';
  model_name: string;
  markdown_content: string;
  has_markdown: boolean;
  generation_metadata: Record<string, unknown>;
  validation_result: Record<string, unknown>;
  source_file_ids: number[];
  sources: { source_file: number; role: string; created_at: string }[];
  approved_by: number | null;
  approved_at: string | null;
  superseded_by: number | null;
  created_at: string;
  updated_at: string;
};

type DashboardFileRow = {
  id: number;
  filename: string;
  error?: string;
  source_kind?: 'upload' | 'website' | 'generated_feedback';
};

type DashboardSummary = {
  compiler: { enabled: boolean };
  source_files: {
    total: number;
    eligible_count: number;
    eligible_file_ids: number[];
    breakdown_by_category: Record<string, number>;
    pending_extraction: DashboardFileRow[];
    extraction_failed: DashboardFileRow[];
    skipped: DashboardFileRow[];
    excluded_by_admin: DashboardFileRow[];
    archived_count: number;
  };
  active_job: CompilationJob | null;
  latest_approved_artifact: Artifact | null;
  latest_draft_artifact: Artifact | null;
  can_compile_now: boolean;
  blocked_reasons: string[];
};

type DashboardCompileResponse = {
  job: CompilationJob;
  selection_snapshot: number[];
};

type CompilationJobListResponse =
  | CompilationJob[]
  | {
      results?: CompilationJob[];
      count?: number;
    };

type UploadFormState = {
  files: File[];
  category: KnowledgeCategory;
  scope: KnowledgeScope;
  include_in_compilation: boolean;
};

type UploadFailure = {
  file: File;
  message: string;
};

type DetailFormState = {
  category: KnowledgeCategory;
  scope: KnowledgeScope;
  include_in_compilation: boolean;
};

type SourceFilePreview = {
  file: KnowledgeSourceFile;
  objectUrl: string;
  mimeType: string;
  textContent: string | null;
};

interface KnowledgeBasesTabProps {
  storeOptions: SearchableSelectOption[];
}

const PAGE_SIZE = 20;
const PROCESSING_STATUSES: ExtractionStatus[] = ['pending', 'in_progress'];
const EXTRACTION_FAST_POLL_MS = 3000;
const EXTRACTION_FAST_POLL_ATTEMPTS = 10;
const EXTRACTION_BACKOFF_POLL_MS = 15000;
const EXTRACTION_BACKOFF_POLL_ATTEMPTS = 10;
const DASHBOARD_POLL_INTERVAL_MS = 4000;
const DASHBOARD_MAX_POLL_MS = 10 * 60 * 1000;
const RECRAWL_MAX_POLL_RETRIES = 4;
const RECRAWL_MAX_POLL_BACKOFF_MS = 20_000;

const statusOptions: Array<{ value: KnowledgeStatus; label: string }> = [
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'processing', label: 'Processing' },
  { value: 'processed', label: 'Processed' },
  { value: 'failed', label: 'Failed' },
  { value: 'archived', label: 'Archived' },
];

const extractionStatusLabels: Record<ExtractionStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  completed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
};

const compilationStageLabels: Record<CompilationJobStatus, string> = {
  pending: 'Queued',
  collecting: 'Collecting files',
  extracting: 'Extracting text',
  synthesizing: 'Synthesizing knowledge base',
  validating: 'Validating draft',
  completed: 'Completed',
  failed: 'Failed',
};

const expectedExtractionStatusByStatus: Partial<
  Record<KnowledgeStatus, ExtractionStatus>
> = {
  uploaded: 'pending',
  processing: 'in_progress',
  processed: 'completed',
  failed: 'failed',
};

const emptyUploadForm: UploadFormState = {
  files: [],
  category: 'other',
  scope: 'unknown',
  include_in_compilation: true,
};

const readSelectedStoreId = () => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem('currentStoreId') || '';
};

const parseResponseError = async (response: Response, fallback: string) => {
  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) return fallback;

  const data = await response.json().catch(() => ({}));
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.message === 'string') return data.message;
  if (typeof data.error === 'string') return data.error;

  const firstFieldError = Object.values(data).find(
    (value) => Array.isArray(value) && value.length > 0,
  );
  if (Array.isArray(firstFieldError)) return String(firstFieldError[0]);

  return fallback;
};

const parseCompilationJobs = (data: CompilationJobListResponse) =>
  Array.isArray(data) ? data : data.results || [];

const isNewerTimestamp = (
  candidate?: string | null,
  previous?: string | null,
) => {
  if (!candidate) return false;
  if (!previous) return true;
  return new Date(candidate).getTime() > new Date(previous).getTime();
};

const formatRelativeTime = (value?: string | null) => {
  if (!value) return 'Never';
  const date = new Date(value);
  const diffSeconds = Math.round((Date.now() - date.getTime()) / 1000);
  const abs = Math.abs(diffSeconds);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31536000],
    ['month', 2592000],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
    ['second', 1],
  ];
  const [unit, seconds] =
    units.find(([, unitSeconds]) => abs >= unitSeconds) ||
    units[units.length - 1];
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
    -Math.round(diffSeconds / seconds),
    unit,
  );
};

const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Never';

const truncateChecksum = (checksum?: string) => {
  if (!checksum) return '-';
  if (checksum.length <= 18) return checksum;
  return `${checksum.slice(0, 10)}...${checksum.slice(-8)}`;
};

const isTextPreviewType = (mimeType: string, filename: string) => {
  const normalizedMime = mimeType.toLowerCase();
  const normalizedName = filename.toLowerCase();
  return (
    normalizedMime.startsWith('text/') ||
    normalizedMime.includes('json') ||
    normalizedMime.includes('csv') ||
    normalizedName.endsWith('.txt') ||
    normalizedName.endsWith('.md') ||
    normalizedName.endsWith('.csv')
  );
};

const isImagePreviewType = (mimeType: string) =>
  mimeType.toLowerCase().startsWith('image/');

const isPdfPreviewType = (mimeType: string, filename: string) =>
  mimeType.toLowerCase().includes('pdf') ||
  filename.toLowerCase().endsWith('.pdf');

const statusBadgeClassName = (status: KnowledgeStatus) => {
  switch (status) {
    case 'processed':
      return 'bg-success-50 text-success-700 ring-success-200 dark:bg-success-900/20 dark:text-success-400 dark:ring-success-800';
    case 'failed':
      return 'bg-error-50 text-error-700 ring-error-200 dark:bg-error-900/20 dark:text-error-400 dark:ring-error-800';
    case 'archived':
      return 'bg-gray-100 text-gray-600 ring-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700';
    case 'processing':
    case 'uploaded':
      return 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700';
    default:
      return 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700';
  }
};

const statusLabel = (status: KnowledgeStatus) =>
  labelFor(statusOptions, status);

const DetailField: React.FC<{
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}> = ({ label, value, mono }) => (
  <div>
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {label}
    </p>
    <div
      className={`mt-1 break-words text-sm font-semibold text-gray-800 dark:text-white/90 ${
        mono ? 'font-mono text-xs' : ''
      }`}
    >
      {value || '-'}
    </div>
  </div>
);

type SourceSortKey =
  | 'original_filename'
  | 'category'
  | 'scope'
  | 'status'
  | 'include_in_compilation'
  | 'extraction_status'
  | 'size_bytes'
  | 'created_at';

const SOURCE_SORT_KEY_TO_FIELD: Record<SourceSortKey, string> = {
  original_filename: 'original_filename',
  category: 'category',
  scope: 'scope',
  status: 'status',
  include_in_compilation: 'include_in_compilation',
  extraction_status: 'extraction_status',
  size_bytes: 'size_bytes',
  created_at: 'created_at',
};

const SOURCE_COLUMNS: { key: SourceSortKey; label: string }[] = [
  { key: 'original_filename', label: 'Filename' },
  { key: 'category', label: 'Category' },
  { key: 'scope', label: 'Scope' },
  { key: 'status', label: 'Status' },
  { key: 'include_in_compilation', label: 'Included' },
  { key: 'extraction_status', label: 'Extraction' },
  { key: 'size_bytes', label: 'Size' },
  { key: 'created_at', label: 'Uploaded' },
];

const KnowledgeBasesTab: React.FC<KnowledgeBasesTabProps> = ({
  storeOptions,
}) => {
  const { user } = useAuth();
  const [selectedStoreId, setSelectedStoreId] = useState(readSelectedStoreId);
  const [sourceFiles, setSourceFiles] = useState<KnowledgeSourceFile[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dashboardSummary, setDashboardSummary] =
    useState<DashboardSummary | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [compileLoading, setCompileLoading] = useState(false);
  const [sourceSort, setSourceSort] = useState<TableSortState<SourceSortKey>>({
    key: null,
    direction: 'desc',
  });
  const sourceOrdering = toOrderingParam(sourceSort, SOURCE_SORT_KEY_TO_FIELD);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [onlyIncluded, setOnlyIncluded] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isWebsiteImportOpen, setIsWebsiteImportOpen] = useState(false);
  const [websiteImportIntent, setWebsiteImportIntent] = useState(0);
  const [uploadForm, setUploadForm] =
    useState<UploadFormState>(emptyUploadForm);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [selectedFile, setSelectedFile] = useState<KnowledgeSourceFile | null>(
    null,
  );
  const [detailForm, setDetailForm] = useState<DetailFormState | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [savingDetail, setSavingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [activeRecrawls, setActiveRecrawls] = useState<
    Record<number, WebsiteRecrawlJobResponse>
  >({});
  const [preview, setPreview] = useState<SourceFilePreview | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'archive' | 'delete';
    file: KnowledgeSourceFile;
  } | null>(null);
  const pollController = useMemo(() => new KnowledgePollController(), []);
  const dashboardPollingStartedAt = useRef<number | null>(null);
  const activeCompileJobId = useRef<number | null>(null);
  const latestDraftSeenAt = useRef<string | null>(null);
  const compilePostInFlight = useRef(false);
  const recrawlGeneration = useRef(0);
  const recrawlStoreId = useRef(selectedStoreId);
  const recrawlRequests = useRef(new Set<AbortController>());

  const selectedStoreLabel = useMemo(
    () =>
      storeOptions.find((option) => String(option.value) === selectedStoreId)
        ?.label || (selectedStoreId ? `Store ID ${selectedStoreId}` : ''),
    [selectedStoreId, storeOptions],
  );

  const detailDirty = useMemo(() => {
    if (!selectedFile || !detailForm) return false;
    return (
      selectedFile.category !== detailForm.category ||
      selectedFile.scope !== detailForm.scope ||
      selectedFile.include_in_compilation !== detailForm.include_in_compilation
    );
  }, [detailForm, selectedFile]);

  const invalidateRecrawlActions = useCallback(() => {
    recrawlGeneration.current += 1;
    recrawlRequests.current.forEach((controller) => controller.abort());
    recrawlRequests.current.clear();
  }, []);

  const isCurrentRecrawlGeneration = useCallback(
    (requestGeneration: number, requestStoreId: string) =>
      requestGeneration === recrawlGeneration.current &&
      requestStoreId === recrawlStoreId.current,
    [],
  );

  const startRecrawlRequest = useCallback(
    (requestGeneration: number, requestStoreId: string) => {
      const controller = new AbortController();
      if (!isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
        controller.abort();
        return controller;
      }
      recrawlRequests.current.add(controller);
      return controller;
    },
    [isCurrentRecrawlGeneration],
  );

  const finishRecrawlRequest = useCallback((controller: AbortController) => {
    recrawlRequests.current.delete(controller);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(
    () => () => {
      invalidateRecrawlActions();
      pollController.cancelAll();
    },
    [invalidateRecrawlActions, pollController],
  );

  useEffect(
    () => () => {
      if (preview?.objectUrl) {
        window.URL.revokeObjectURL(preview.objectUrl);
      }
    },
    [preview?.objectUrl],
  );

  useEffect(() => {
    if (!openActionMenuId) return undefined;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('[data-knowledge-action-menu]')
      ) {
        return;
      }
      setOpenActionMenuId(null);
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [openActionMenuId]);

  const fetchLatestFailedCompileJob = useCallback(async () => {
    try {
      const response = await api.get(
        apiEndpoints.getKnowledgeBaseCompilationJobs({ status: 'failed' }),
      );
      if (response?.ok) {
        const jobs = parseCompilationJobs(await response.json());
        return jobs.sort(
          (a, b) =>
            new Date(b.completed_at || b.created_at).getTime() -
            new Date(a.completed_at || a.created_at).getTime(),
        )[0];
      }
    } catch (err) {
      console.error('Knowledge compile failed-job load error:', err);
    }
    return null;
  }, []);

  const handleCompileFinished = useCallback(
    async (summary: DashboardSummary, previousDraftAt: string | null) => {
      activeCompileJobId.current = null;
      dashboardPollingStartedAt.current = null;

      if (
        isNewerTimestamp(
          summary.latest_draft_artifact?.created_at,
          previousDraftAt,
        )
      ) {
        setSuccess('New draft ready for review.');
        return;
      }

      const failedJob = await fetchLatestFailedCompileJob();
      setDashboardError(
        failedJob?.error_message ||
          'Compilation finished without a new draft. The job may have failed.',
      );
    },
    [fetchLatestFailedCompileJob],
  );

  const fetchDashboardSummary = useCallback(
    async (options?: { silent?: boolean; fromPoll?: boolean }) => {
      const silent = options?.silent === true;
      if (!user?.is_platform_admin || !selectedStoreId) {
        setDashboardSummary(null);
        return null;
      }

      if (!silent) setLoadingDashboard(true);
      setDashboardError('');

      try {
        const response = await api.get(
          apiEndpoints.getKnowledgeBaseDashboardCompile(),
        );
        if (response?.ok) {
          const data: DashboardSummary = await response.json();
          if (compilePostInFlight.current && !data.active_job) {
            return data;
          }

          const previousActiveJobId = activeCompileJobId.current;
          const previousDraftAt = latestDraftSeenAt.current;
          setDashboardSummary(data);

          if (data.active_job) {
            activeCompileJobId.current = data.active_job.id;
            if (!dashboardPollingStartedAt.current) {
              dashboardPollingStartedAt.current = Date.now();
            }
            if (!latestDraftSeenAt.current) {
              latestDraftSeenAt.current =
                data.latest_draft_artifact?.created_at || null;
            }
          } else {
            if (options?.fromPoll && previousActiveJobId) {
              await handleCompileFinished(data, previousDraftAt);
            }
            latestDraftSeenAt.current =
              data.latest_draft_artifact?.created_at || null;
          }

          return data;
        }

        if (response) {
          const message =
            response.status === 403
              ? "You don't have permission to view this."
              : await parseResponseError(
                  response,
                  `Failed to load knowledge dashboard (Status: ${response.status})`,
                );
          setDashboardError(message);
        } else {
          setDashboardError('Failed to load knowledge dashboard: No response.');
        }
      } catch (err) {
        console.error('Knowledge dashboard load error:', err);
        setDashboardError(
          'An unexpected error occurred loading the dashboard.',
        );
      } finally {
        if (!silent) setLoadingDashboard(false);
      }

      return null;
    },
    [handleCompileFinished, selectedStoreId, user?.is_platform_admin],
  );

  const fetchSourceFiles = useCallback(
    async (options?: { silent?: boolean }) => {
      const silent = options?.silent === true;
      if (!user?.is_platform_admin || !selectedStoreId) {
        setSourceFiles([]);
        setTotalCount(0);
        setTotalPages(1);
        return [];
      }

      if (!silent) setLoading(true);
      setError('');
      try {
        const response = await api.get(
          apiEndpoints.getKnowledgeBaseSourceFiles({
            page,
            page_size: PAGE_SIZE,
            category: categoryFilter || undefined,
            scope: scopeFilter || undefined,
            status: statusFilter || undefined,
            include_archived: showArchived,
            include_in_compilation: onlyIncluded ? true : undefined,
            search: debouncedSearch || undefined,
            ordering: sourceOrdering ?? undefined,
          }),
        );

        if (response?.ok) {
          const data: KnowledgeSourceListResponse = await response.json();
          const results = Array.isArray(data.results) ? data.results : [];
          setSourceFiles(results);
          setTotalCount(data.count || results.length);
          setTotalPages(data.total_pages || 1);
          return results;
        }

        if (response) {
          const message =
            response.status === 403
              ? "You don't have permission to view this."
              : await parseResponseError(
                  response,
                  `Failed to load knowledge sources (Status: ${response.status})`,
                );
          setError(message);
        } else {
          setError('Failed to load knowledge sources: No response from server');
        }
      } catch (err) {
        console.error('Knowledge source load error:', err);
        setError('An unexpected error occurred loading knowledge sources.');
      } finally {
        if (!silent) setLoading(false);
      }

      return [];
    },
    [
      categoryFilter,
      debouncedSearch,
      sourceOrdering,
      onlyIncluded,
      page,
      scopeFilter,
      selectedStoreId,
      showArchived,
      statusFilter,
      user?.is_platform_admin,
    ],
  );

  useEffect(() => {
    fetchSourceFiles();
    fetchDashboardSummary();
  }, [fetchDashboardSummary, fetchSourceFiles]);

  useEffect(() => {
    const shouldPollDashboard =
      !!dashboardSummary?.active_job ||
      !!dashboardSummary?.source_files.pending_extraction.length;
    if (!shouldPollDashboard || !selectedStoreId) return undefined;

    if (!dashboardPollingStartedAt.current) {
      dashboardPollingStartedAt.current = Date.now();
    }

    const elapsed = Date.now() - dashboardPollingStartedAt.current;
    if (elapsed >= DASHBOARD_MAX_POLL_MS) {
      setDashboardError('Still working - refresh to check status.');
      return undefined;
    }

    const operationId =
      dashboardSummary?.active_job?.id || 'pending-extraction';
    return pollController.schedule(
      selectedStoreId,
      'dashboard',
      operationId,
      DASHBOARD_POLL_INTERVAL_MS,
      () => fetchDashboardSummary({ silent: true, fromPoll: true }),
    );
  }, [
    dashboardSummary?.active_job,
    dashboardSummary?.source_files.pending_extraction.length,
    fetchDashboardSummary,
    pollController,
    selectedStoreId,
  ]);

  const mergeSourceFile = useCallback((file: KnowledgeSourceFile) => {
    setSourceFiles((prev) => {
      if (prev.some((row) => row.id === file.id)) {
        return prev.map((row) => (row.id === file.id ? file : row));
      }
      return [file, ...prev];
    });
  }, []);

  const fetchSourceFileForPolling = useCallback(
    async (id: number, signal?: AbortSignal) => {
      try {
        const response = await api.get(
          apiEndpoints.getKnowledgeBaseSourceFile(id),
          { signal },
        );
        if (response?.ok) {
          return (await response.json()) as KnowledgeSourceFile;
        }

        if (response?.status === 404) {
          setError('This file is no longer available.');
          await fetchSourceFiles({ silent: true });
          return null;
        }

        if (response) {
          setError(
            await parseResponseError(
              response,
              `Failed to refresh source file (Status: ${response.status})`,
            ),
          );
        }
      } catch (err) {
        if ((err as DOMException).name === 'AbortError') return null;
        console.error('Knowledge source polling error:', err);
        setError('An unexpected error occurred refreshing this file.');
      }

      return null;
    },
    [fetchSourceFiles],
  );

  const fetchDetail = useCallback(
    async (id: number) => {
      setLoadingDetail(true);
      setDetailError('');
      try {
        const response = await api.get(
          apiEndpoints.getKnowledgeBaseSourceFile(id),
        );
        if (response?.ok) {
          const data: KnowledgeSourceFile = await response.json();
          setSelectedFile(data);
          setDetailForm({
            category: data.category,
            scope: data.scope,
            include_in_compilation: data.include_in_compilation,
          });
          return data;
        }

        if (response?.status === 404) {
          setSelectedFile(null);
          setDetailForm(null);
          setError('This file is no longer available.');
          fetchSourceFiles();
          return null;
        }

        if (response) {
          setDetailError(
            await parseResponseError(
              response,
              `Failed to load source file (Status: ${response.status})`,
            ),
          );
        }
      } catch (err) {
        console.error('Knowledge source detail error:', err);
        setDetailError('An unexpected error occurred loading this file.');
      } finally {
        setLoadingDetail(false);
      }
      return null;
    },
    [fetchSourceFiles],
  );

  const startPollingList = useCallback(
    (
      targetId: number,
      attempts = EXTRACTION_FAST_POLL_ATTEMPTS,
      phase: 'fast' | 'backoff' = 'fast',
    ) => {
      if (attempts <= 0) return;
      const delay =
        phase === 'fast' ? EXTRACTION_FAST_POLL_MS : EXTRACTION_BACKOFF_POLL_MS;
      pollController.schedule(
        selectedStoreId,
        'source',
        targetId,
        delay,
        async (signal) => {
          const target = await fetchSourceFileForPolling(targetId, signal);
          if (signal.aborted) return;
          if (target) mergeSourceFile(target);

          const stillProcessing =
            target && PROCESSING_STATUSES.includes(target.extraction_status);
          if (stillProcessing && attempts > 1) {
            startPollingList(targetId, attempts - 1, phase);
          } else if (stillProcessing && phase === 'fast') {
            startPollingList(
              targetId,
              EXTRACTION_BACKOFF_POLL_ATTEMPTS,
              'backoff',
            );
          } else {
            await fetchSourceFiles({ silent: true });
            await fetchDashboardSummary({ silent: true });
          }
        },
      );
    },
    [
      fetchDashboardSummary,
      fetchSourceFileForPolling,
      fetchSourceFiles,
      mergeSourceFile,
      pollController,
      selectedStoreId,
    ],
  );

  const startPollingDetail = useCallback(
    (
      targetId: number,
      attempts = EXTRACTION_FAST_POLL_ATTEMPTS,
      phase: 'fast' | 'backoff' = 'fast',
    ) => {
      if (attempts <= 0) return;
      const delay =
        phase === 'fast' ? EXTRACTION_FAST_POLL_MS : EXTRACTION_BACKOFF_POLL_MS;
      pollController.schedule(
        selectedStoreId,
        'source',
        targetId,
        delay,
        async (signal) => {
          const file = await fetchSourceFileForPolling(targetId, signal);
          if (signal.aborted) return;
          if (file) {
            mergeSourceFile(file);
            setSelectedFile(file);
            setDetailForm({
              category: file.category,
              scope: file.scope,
              include_in_compilation: file.include_in_compilation,
            });
          }

          const stillProcessing =
            file && PROCESSING_STATUSES.includes(file.extraction_status);
          if (stillProcessing && attempts > 1) {
            startPollingDetail(targetId, attempts - 1, phase);
          } else if (stillProcessing && phase === 'fast') {
            startPollingDetail(
              targetId,
              EXTRACTION_BACKOFF_POLL_ATTEMPTS,
              'backoff',
            );
          } else {
            await fetchSourceFiles({ silent: true });
            await fetchDashboardSummary({ silent: true });
          }
        },
      );
    },
    [
      fetchDashboardSummary,
      fetchSourceFileForPolling,
      fetchSourceFiles,
      mergeSourceFile,
      pollController,
      selectedStoreId,
    ],
  );

  const handleStoreChange = (value: string) => {
    invalidateRecrawlActions();
    pollController.cancelAll();
    recrawlStoreId.current = value;
    setActiveRecrawls({});
    setActionLoadingId(null);
    setIsWebsiteImportOpen(false);
    setWebsiteImportIntent((current) => current + 1);
    setSelectedStoreId(value);
    setPage(1);
    setError('');
    setSuccess('');
    setSuccess('');
    setDashboardSummary(null);
    setDashboardError('');
    latestDraftSeenAt.current = null;
    activeCompileJobId.current = null;
    dashboardPollingStartedAt.current = null;
    setSelectedFile(null);
    setDetailForm(null);
    setPreview(null);
    setPreviewError('');

    if (typeof window === 'undefined') return;
    if (value) {
      localStorage.setItem('currentStoreId', value);
    } else {
      localStorage.removeItem('currentStoreId');
    }
  };

  const openWebsiteImport = () => {
    setWebsiteImportIntent((current) => current + 1);
    setIsWebsiteImportOpen(true);
  };

  const resetUploadForm = () => {
    setUploadForm(emptyUploadForm);
    setUploadError('');
  };

  const startDashboardCompile = async () => {
    if (!dashboardSummary?.can_compile_now || compileLoading) return;

    setCompileLoading(true);
    setDashboardError('');
    setSuccess('');
    compilePostInFlight.current = true;
    latestDraftSeenAt.current =
      dashboardSummary.latest_draft_artifact?.created_at || null;

    try {
      const response = await api.post(
        apiEndpoints.startKnowledgeBaseDashboardCompile(),
        {},
      );
      if (response?.status === 201) {
        const data: DashboardCompileResponse = await response.json();
        activeCompileJobId.current = data.job.id;
        dashboardPollingStartedAt.current = Date.now();
        setDashboardSummary((prev) =>
          prev ? { ...prev, active_job: data.job } : prev,
        );
        setSuccess(
          `Compilation started with ${data.selection_snapshot.length} file${
            data.selection_snapshot.length === 1 ? '' : 's'
          }.`,
        );
        await fetchDashboardSummary({ silent: true });
      } else if (response?.status === 409) {
        setDashboardError(
          await parseResponseError(
            response,
            'Another compile is already running for this store.',
          ),
        );
        await fetchDashboardSummary({ silent: true });
      } else if (response?.status === 400) {
        setDashboardError(
          await parseResponseError(
            response,
            'No eligible files are ready to compile. Upload a file first.',
          ),
        );
        await fetchDashboardSummary({ silent: true });
      } else if (response?.status === 403) {
        setDashboardError("You don't have permission to compile this store.");
      } else if (response?.status === 503) {
        setDashboardError('Knowledge base compilation is currently disabled.');
        await fetchDashboardSummary({ silent: true });
      } else if (response) {
        setDashboardError(
          await parseResponseError(
            response,
            "Couldn't start compile - try again.",
          ),
        );
        await fetchDashboardSummary({ silent: true });
      } else {
        setDashboardError('Failed to start compile: No response from server.');
      }
    } catch (err) {
      console.error('Knowledge dashboard compile error:', err);
      setDashboardError('An unexpected error occurred starting compilation.');
    } finally {
      compilePostInFlight.current = false;
      setCompileLoading(false);
    }
  };

  const includeSourceFile = async (file: DashboardFileRow) => {
    setActionLoadingId(file.id);
    setDashboardError('');
    setSuccess('');
    try {
      const response = await api.patch(
        apiEndpoints.updateKnowledgeBaseSourceFile(file.id),
        { include_in_compilation: true },
      );
      if (response?.ok) {
        setSuccess(`Included ${file.filename} in compilation.`);
        await fetchDashboardSummary({ silent: true });
        await fetchSourceFiles({ silent: true });
      } else if (response) {
        setDashboardError(
          await parseResponseError(
            response,
            `Failed to include source file (Status: ${response.status})`,
          ),
        );
      } else {
        setDashboardError('Failed to include source file: No response.');
      }
    } catch (err) {
      console.error('Knowledge source include error:', err);
      setDashboardError('An unexpected error occurred including this file.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleUpload = async () => {
    setUploadError('');
    const validationError = await validateUploadFiles(uploadForm.files);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    if (!selectedStoreId) {
      setUploadError('Select a store before uploading a knowledge source.');
      return;
    }

    setUploading(true);
    const uploadedFiles: KnowledgeSourceFile[] = [];
    const failedUploads: UploadFailure[] = [];

    try {
      // Keep uploads sequential so large PDFs do not enqueue extraction work all at once.
      for (const file of uploadForm.files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', uploadForm.category);
        formData.append('scope', uploadForm.scope);
        formData.append(
          'include_in_compilation',
          uploadForm.include_in_compilation ? 'true' : 'false',
        );

        try {
          const response = await api.postForm(
            apiEndpoints.getKnowledgeBaseSourceFiles(),
            formData,
          );
          if (response?.ok) {
            uploadedFiles.push((await response.json()) as KnowledgeSourceFile);
          } else if (response) {
            failedUploads.push({
              file,
              message: await parseResponseError(
                response,
                `Failed to upload file (Status: ${response.status})`,
              ),
            });
          } else {
            failedUploads.push({
              file,
              message: 'No response from server',
            });
          }
        } catch (err) {
          console.error('Knowledge source upload error:', err);
          failedUploads.push({
            file,
            message: 'Unexpected upload error',
          });
        }
      }

      if (uploadedFiles.length) {
        const uploadedCount = uploadedFiles.length;
        const totalCount = uploadForm.files.length;
        setSuccess(
          failedUploads.length
            ? `Uploaded ${uploadedCount} of ${totalCount} files.`
            : `Uploaded ${uploadedCount} file${uploadedCount === 1 ? '' : 's'}.`,
        );
        if (!failedUploads.length) {
          setIsUploadOpen(false);
          resetUploadForm();
        } else {
          setUploadForm((prev) => ({
            ...prev,
            files: failedUploads.map((failure) => failure.file),
          }));
        }
        setPage(1);
        uploadedFiles.forEach(mergeSourceFile);
        await fetchSourceFiles({ silent: true });
        await fetchDashboardSummary({ silent: true });
        uploadedFiles
          .filter((file) =>
            PROCESSING_STATUSES.includes(file.extraction_status),
          )
          .forEach((file) => startPollingList(file.id));
      }

      if (failedUploads.length) {
        const failureMessage = failedUploads
          .map((failure) => `${failure.file.name}: ${failure.message}`)
          .join(' | ');
        setUploadError(
          uploadedFiles.length ? `Failed: ${failureMessage}` : failureMessage,
        );
      }
    } catch (err) {
      console.error('Knowledge source upload error:', err);
      setUploadError('An unexpected error occurred uploading the files.');
    } finally {
      setUploading(false);
    }
  };

  const saveDetail = async () => {
    if (!selectedFile || !detailForm || !detailDirty) return;

    setSavingDetail(true);
    setDetailError('');
    try {
      const response = await api.patch(
        apiEndpoints.updateKnowledgeBaseSourceFile(selectedFile.id),
        detailForm,
      );
      if (response?.ok) {
        const data: KnowledgeSourceFile = await response.json();
        setSelectedFile(data);
        setDetailForm({
          category: data.category,
          scope: data.scope,
          include_in_compilation: data.include_in_compilation,
        });
        setSuccess(`Saved ${data.original_filename}`);
        await fetchSourceFiles({ silent: true });
        await fetchDashboardSummary({ silent: true });
      } else if (response?.status === 404) {
        setSelectedFile(null);
        setDetailForm(null);
        setError('This file is no longer available.');
        fetchSourceFiles();
        fetchDashboardSummary({ silent: true });
      } else if (response) {
        setDetailError(
          await parseResponseError(
            response,
            `Failed to save source file (Status: ${response.status})`,
          ),
        );
      }
    } catch (err) {
      console.error('Knowledge source save error:', err);
      setDetailError('An unexpected error occurred saving this file.');
    } finally {
      setSavingDetail(false);
    }
  };

  const downloadFile = async (file: KnowledgeSourceFile) => {
    setActionLoadingId(file.id);
    setError('');
    try {
      const response = await api.get(
        apiEndpoints.downloadKnowledgeBaseSourceFile(file.id),
      );
      if (response?.ok) {
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = file.original_filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      } else if (response?.status === 404) {
        setError('This file is no longer available.');
        fetchSourceFiles();
      } else if (response) {
        setError(
          await parseResponseError(
            response,
            `Failed to download file (Status: ${response.status})`,
          ),
        );
      }
    } catch (err) {
      console.error('Knowledge source download error:', err);
      setError('An unexpected error occurred downloading this file.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const previewFile = async (file: KnowledgeSourceFile) => {
    setActionLoadingId(file.id);
    setPreviewError('');
    setError('');
    setDetailError('');

    try {
      const response = await api.get(
        apiEndpoints.previewKnowledgeBaseSourceFile(file.id),
      );
      if (response?.ok) {
        const blob = await response.blob();
        const mimeType =
          response.headers.get('content-type') || blob.type || file.mime_type;
        const objectUrl = window.URL.createObjectURL(blob);
        const textContent = isTextPreviewType(mimeType, file.original_filename)
          ? await blob.text()
          : null;

        setPreview({
          file,
          objectUrl,
          mimeType,
          textContent,
        });
      } else if (response?.status === 404) {
        setError('This file is no longer available.');
        fetchSourceFiles();
      } else if (response) {
        const message = await parseResponseError(
          response,
          `Failed to preview file (Status: ${response.status})`,
        );
        if (selectedFile?.id === file.id) setDetailError(message);
        else setError(message);
      } else {
        setError('Failed to preview file: No response from server');
      }
    } catch (err) {
      console.error('Knowledge source preview error:', err);
      const message = 'An unexpected error occurred previewing this file.';
      if (selectedFile?.id === file.id) setDetailError(message);
      else setError(message);
    } finally {
      setActionLoadingId(null);
    }
  };

  const downloadPreview = () => {
    if (!preview) return;
    const link = document.createElement('a');
    link.href = preview.objectUrl;
    link.download = preview.file.original_filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const reprocessFile = async (
    file: Pick<KnowledgeSourceFile, 'id' | 'original_filename'>,
  ) => {
    setActionLoadingId(file.id);
    setError('');
    setDetailError('');
    try {
      const response = await api.post(
        apiEndpoints.reprocessKnowledgeBaseSourceFile(file.id),
      );
      if (response?.ok) {
        const data: KnowledgeSourceFile = await response.json();
        setSuccess(`Queued reprocessing for ${data.original_filename}`);
        await fetchSourceFiles();
        await fetchDashboardSummary({ silent: true });
        if (selectedFile?.id === data.id) {
          setSelectedFile(data);
          setDetailForm({
            category: data.category,
            scope: data.scope,
            include_in_compilation: data.include_in_compilation,
          });
          startPollingDetail(data.id);
        } else {
          startPollingList(data.id);
        }
      } else if (response?.status === 404) {
        setError('This file is no longer available.');
        fetchSourceFiles();
      } else if (response) {
        const message = await parseResponseError(
          response,
          `Failed to reprocess file (Status: ${response.status})`,
        );
        if (selectedFile?.id === file.id) setDetailError(message);
        else setError(message);
        if (response.status === 409) {
          await fetchDashboardSummary({ silent: true });
        }
      }
    } catch (err) {
      console.error('Knowledge source reprocess error:', err);
      setError('An unexpected error occurred reprocessing this file.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const pollRecrawl = (
    source: Pick<KnowledgeSourceFile, 'id' | 'original_filename'>,
    jobId: number,
    requestGeneration: number,
    requestStoreId: string,
    failedAttempts = 0,
    delayMs = EXTRACTION_FAST_POLL_MS,
  ) => {
    if (!isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) return;
    pollController.schedule(
      requestStoreId,
      'recrawl',
      jobId,
      delayMs,
      async (signal) => {
        if (!isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
          return;
        }
        try {
          const response = await api.get(
            apiEndpoints.getKnowledgeBaseWebsiteRecrawlJob(jobId),
            { signal },
          );
          if (!isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
            return;
          }
          if (!response?.ok) {
            if (
              (!response ||
                response.status === 408 ||
                response.status === 425 ||
                response.status === 429 ||
                response.status >= 500) &&
              failedAttempts < RECRAWL_MAX_POLL_RETRIES
            ) {
              setError('Recrawl status is temporarily unavailable. Retrying…');
              pollRecrawl(
                source,
                jobId,
                requestGeneration,
                requestStoreId,
                failedAttempts + 1,
                boundedPollBackoff(
                  EXTRACTION_FAST_POLL_MS,
                  failedAttempts,
                  RECRAWL_MAX_POLL_BACKOFF_MS,
                ),
              );
            } else if (response) {
              const message = await parseResponseError(
                response,
                'Recrawl polling failed.',
              );
              if (
                isCurrentRecrawlGeneration(requestGeneration, requestStoreId)
              ) {
                setError(message);
              }
            } else {
              setError('Recrawl polling failed after several retries.');
            }
            return;
          }
          const job = (await response.json()) as WebsiteRecrawlJobResponse;
          if (!isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
            return;
          }
          setActiveRecrawls((current) => ({ ...current, [source.id]: job }));
          if (isWebsiteOperationTerminal(job.status)) {
            setActiveRecrawls((current) => {
              const next = { ...current };
              delete next[source.id];
              return next;
            });
            if (job.result === 'activated') {
              setSuccess(
                `Updated ${source.original_filename} from its website.`,
              );
            } else if (job.result === 'unchanged') {
              setSuccess(`${source.original_filename} is already current.`);
            } else if (job.result === 'deferred_conflict') {
              setError(
                `${source.original_filename} is ready but activation is deferred until compilation finishes.`,
              );
            } else if (job.status === 'failed') {
              setError(job.error_message || 'Website recrawl failed.');
            } else if (job.status === 'cancelled') {
              setSuccess(`Cancelled recrawl for ${source.original_filename}.`);
            }
            await fetchSourceFiles({ silent: true });
            if (
              !isCurrentRecrawlGeneration(requestGeneration, requestStoreId)
            ) {
              return;
            }
            await fetchDashboardSummary({ silent: true });
            return;
          }
          pollRecrawl(source, jobId, requestGeneration, requestStoreId);
        } catch (err) {
          if (
            (err as DOMException).name !== 'AbortError' &&
            isCurrentRecrawlGeneration(requestGeneration, requestStoreId)
          ) {
            if (failedAttempts < RECRAWL_MAX_POLL_RETRIES) {
              setError('Recrawl status is temporarily unavailable. Retrying…');
              pollRecrawl(
                source,
                jobId,
                requestGeneration,
                requestStoreId,
                failedAttempts + 1,
                boundedPollBackoff(
                  EXTRACTION_FAST_POLL_MS,
                  failedAttempts,
                  RECRAWL_MAX_POLL_BACKOFF_MS,
                ),
              );
            } else {
              setError('Website recrawl polling failed.');
            }
          }
        }
      },
    );
  };

  const recrawlFile = async (
    file: Pick<KnowledgeSourceFile, 'id' | 'original_filename'>,
  ) => {
    const requestGeneration = recrawlGeneration.current;
    const requestStoreId = recrawlStoreId.current;
    const controller = startRecrawlRequest(requestGeneration, requestStoreId);
    if (controller.signal.aborted) return;
    setActionLoadingId(file.id);
    setError('');
    try {
      const response = await api.post(
        apiEndpoints.recrawlKnowledgeBaseSourceFile(file.id),
        { force_reextract: false },
        { signal: controller.signal },
      );
      if (!isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
        return;
      }
      if (response?.status === 202) {
        const job = (await response.json()) as WebsiteRecrawlJobResponse;
        if (!isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
          return;
        }
        setActiveRecrawls((current) => ({ ...current, [file.id]: job }));
        setSuccess(`Queued website recrawl for ${file.original_filename}.`);
        pollRecrawl(file, job.id, requestGeneration, requestStoreId);
      } else if (response) {
        const message = await parseResponseError(
          response,
          response.status === 409
            ? 'This website source already has an active operation.'
            : 'Could not start website recrawl.',
        );
        if (isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
          setError(message);
        }
      }
    } catch (requestError) {
      if (
        (requestError as DOMException).name !== 'AbortError' &&
        isCurrentRecrawlGeneration(requestGeneration, requestStoreId)
      ) {
        setError('Could not start website recrawl.');
      }
    } finally {
      finishRecrawlRequest(controller);
      if (isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
        setActionLoadingId(null);
      }
    }
  };

  const cancelRecrawl = async (file: KnowledgeSourceFile) => {
    const activeJob = activeRecrawls[file.id];
    if (!activeJob || isWebsiteOperationTerminal(activeJob.status)) return;
    const requestGeneration = recrawlGeneration.current;
    const requestStoreId = recrawlStoreId.current;
    const controller = startRecrawlRequest(requestGeneration, requestStoreId);
    if (controller.signal.aborted) return;
    setActionLoadingId(file.id);
    try {
      const response = await api.post(
        apiEndpoints.cancelKnowledgeBaseWebsiteRecrawlJob(activeJob.id),
        {},
        { signal: controller.signal },
      );
      if (!isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
        return;
      }
      if (response?.ok) {
        const job = (await response
          .json()
          .catch(() => activeJob)) as WebsiteRecrawlJobResponse;
        if (!isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
          return;
        }
        if (isWebsiteOperationTerminal(job.status)) {
          setActiveRecrawls((current) => {
            const next = { ...current };
            delete next[file.id];
            return next;
          });
          if (job.status === 'cancelled') {
            setSuccess(`Cancelled recrawl for ${file.original_filename}.`);
          }
        } else {
          setActiveRecrawls((current) => ({ ...current, [file.id]: job }));
          setSuccess(
            `Cancellation requested for recrawl of ${file.original_filename}.`,
          );
        }
      } else if (response) {
        const message = await parseResponseError(
          response,
          'Could not cancel recrawl.',
        );
        if (isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
          setError(message);
        }
      }
    } catch (requestError) {
      if (
        (requestError as DOMException).name !== 'AbortError' &&
        isCurrentRecrawlGeneration(requestGeneration, requestStoreId)
      ) {
        setError('Could not cancel recrawl.');
      }
    } finally {
      finishRecrawlRequest(controller);
      if (isCurrentRecrawlGeneration(requestGeneration, requestStoreId)) {
        setActionLoadingId(null);
      }
    }
  };

  const archiveFile = async (file: KnowledgeSourceFile) => {
    setActionLoadingId(file.id);
    setError('');
    setDetailError('');
    try {
      const response = await api.post(
        apiEndpoints.archiveKnowledgeBaseSourceFile(file.id),
      );
      if (response?.ok) {
        const data: KnowledgeSourceFile = await response.json();
        setSuccess(`Archived ${data.original_filename}`);
        setConfirmAction(null);
        await fetchSourceFiles();
        await fetchDashboardSummary({ silent: true });
        if (selectedFile?.id === data.id) {
          setSelectedFile(data);
          setDetailForm({
            category: data.category,
            scope: data.scope,
            include_in_compilation: data.include_in_compilation,
          });
        }
      } else if (response?.status === 404) {
        setError('This file is no longer available.');
        setConfirmAction(null);
        setSelectedFile(null);
        setDetailForm(null);
        fetchSourceFiles();
        fetchDashboardSummary({ silent: true });
      } else if (response) {
        const message = await parseResponseError(
          response,
          `Failed to archive file (Status: ${response.status})`,
        );
        if (selectedFile?.id === file.id) setDetailError(message);
        else setError(message);
        if (response.status === 409) {
          await fetchDashboardSummary({ silent: true });
        }
      }
    } catch (err) {
      console.error('Knowledge source archive error:', err);
      const message = 'An unexpected error occurred archiving this file.';
      if (selectedFile?.id === file.id) setDetailError(message);
      else setError(message);
    } finally {
      setActionLoadingId(null);
    }
  };

  const unarchiveFile = async (file: KnowledgeSourceFile) => {
    setActionLoadingId(file.id);
    setError('');
    setDetailError('');
    try {
      const response = await api.post(
        apiEndpoints.unarchiveKnowledgeBaseSourceFile(file.id),
      );
      if (response?.ok) {
        const data: KnowledgeSourceFile = await response.json();
        setSuccess(
          `Unarchived ${data.original_filename}. Include it in compilation when ready.`,
        );
        await fetchSourceFiles({ silent: true });
        await fetchDashboardSummary({ silent: true });
        if (selectedFile?.id === data.id) {
          setSelectedFile(data);
          setDetailForm({
            category: data.category,
            scope: data.scope,
            include_in_compilation: data.include_in_compilation,
          });
        }
      } else if (response?.status === 404) {
        setError('This file is no longer available.');
        setSelectedFile(null);
        setDetailForm(null);
        fetchSourceFiles();
        fetchDashboardSummary({ silent: true });
      } else if (response) {
        const message = await parseResponseError(
          response,
          `Failed to unarchive file (Status: ${response.status})`,
        );
        if (selectedFile?.id === file.id) setDetailError(message);
        else setError(message);
        if (response.status === 409) {
          await fetchDashboardSummary({ silent: true });
        }
      }
    } catch (err) {
      console.error('Knowledge source unarchive error:', err);
      const message = 'An unexpected error occurred unarchiving this file.';
      if (selectedFile?.id === file.id) setDetailError(message);
      else setError(message);
    } finally {
      setActionLoadingId(null);
    }
  };

  const deleteFile = async (file: KnowledgeSourceFile) => {
    setActionLoadingId(file.id);
    setError('');
    setDetailError('');
    try {
      const response = await api.delete(
        apiEndpoints.deleteKnowledgeBaseSourceFile(file.id),
      );
      if (response?.status === 204) {
        setSuccess(`Deleted ${file.original_filename}`);
        setConfirmAction(null);
        if (selectedFile?.id === file.id) {
          setSelectedFile(null);
          setDetailForm(null);
        }
        await fetchSourceFiles({ silent: true });
        await fetchDashboardSummary({ silent: true });
      } else if (response?.status === 502) {
        const message = 'Storage cleanup failed. Please retry in a moment.';
        if (selectedFile?.id === file.id) setDetailError(message);
        else setError(message);
      } else if (response?.status === 404) {
        setError('This file is no longer available.');
        setConfirmAction(null);
        setSelectedFile(null);
        setDetailForm(null);
        fetchSourceFiles();
        fetchDashboardSummary({ silent: true });
      } else if (response) {
        const message = await parseResponseError(
          response,
          `Failed to delete file (Status: ${response.status})`,
        );
        if (selectedFile?.id === file.id) setDetailError(message);
        else setError(message);
        if (response.status === 409) {
          await fetchDashboardSummary({ silent: true });
        }
      }
    } catch (err) {
      console.error('Knowledge source delete error:', err);
      const message = 'An unexpected error occurred deleting this file.';
      if (selectedFile?.id === file.id) setDetailError(message);
      else setError(message);
    } finally {
      setActionLoadingId(null);
    }
  };

  const renderExtractionBadge = (
    file: KnowledgeSourceFile,
    alwaysShow = false,
  ) => {
    const expectedExtractionStatus =
      expectedExtractionStatusByStatus[file.status];
    const differsFromLifecycle =
      !!expectedExtractionStatus &&
      file.extraction_status !== expectedExtractionStatus;
    const isInProgress = file.extraction_status === 'in_progress';

    if (!alwaysShow && !differsFromLifecycle && !isInProgress) {
      return <span className="text-gray-400 dark:text-gray-500">-</span>;
    }

    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700">
        {isInProgress ? (
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-brand-500" />
        ) : null}
        {extractionStatusLabels[file.extraction_status]}
      </span>
    );
  };

  const renderArtifactLink = (artifact: Artifact | null, label: string) => {
    if (!artifact) return <span className="text-gray-400">None</span>;
    return (
      <a
        href={`/artifacts/${artifact.id}/`}
        className="font-semibold text-brand-600 underline-offset-2 hover:underline dark:text-brand-400"
      >
        {label} #{artifact.id}
      </a>
    );
  };

  const renderDashboardBucket = (
    title: string,
    rows: DashboardFileRow[],
    kind: 'failed' | 'skipped' | 'excluded' | 'pending',
    description?: string,
  ) => {
    if (!rows.length) return null;

    return (
      <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-800">
        <div className="mb-3">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90">
            {title}
          </h4>
          {description ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {description}
            </p>
          ) : null}
        </div>
        <div className="space-y-2">
          {rows.map((row) => (
            <div
              key={`${kind}-${row.id}`}
              className="flex flex-col gap-2 rounded-xl bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between dark:bg-gray-900/50"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-800 dark:text-white/90">
                  {row.filename}
                </p>
                {row.error ? (
                  <p className="mt-1 text-xs text-error-500">{row.error}</p>
                ) : null}
              </div>
              {kind === 'failed' ? (
                <button
                  type="button"
                  onClick={() =>
                    row.source_kind === 'website'
                      ? recrawlFile({
                          id: row.id,
                          original_filename: row.filename,
                        })
                      : reprocessFile({
                          id: row.id,
                          original_filename: row.filename,
                        })
                  }
                  disabled={actionLoadingId === row.id}
                  className="shrink-0 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  {actionLoadingId === row.id
                    ? 'Working...'
                    : row.source_kind === 'website'
                      ? 'Recrawl'
                      : 'Reprocess'}
                </button>
              ) : null}
              {kind === 'skipped' ? (
                <button
                  type="button"
                  onClick={() => {
                    resetUploadForm();
                    setIsUploadOpen(true);
                  }}
                  className="shrink-0 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-white dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  Re-upload as PDF/text
                </button>
              ) : null}
              {kind === 'excluded' ? (
                <button
                  type="button"
                  onClick={() => includeSourceFile(row)}
                  disabled={actionLoadingId === row.id}
                  className="shrink-0 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  {actionLoadingId === row.id ? 'Working...' : 'Include'}
                </button>
              ) : null}
              {kind === 'pending' ? (
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-brand-500" />
                  Processing...
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!user?.is_platform_admin) {
    return (
      <ComponentCard title="Knowledge Bases" className="overflow-hidden">
        <div className="p-4 text-sm text-error-500">
          This tool is only available to platform admins.
        </div>
      </ComponentCard>
    );
  }

  return (
    <div className="space-y-6">
      <ComponentCard title="Knowledge Base Store" className="overflow-hidden">
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <Label>Selected Store</Label>
            <SearchableSelect
              options={storeOptions}
              value={selectedStoreId}
              onChange={handleStoreChange}
              placeholder="Select a store"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Knowledge source requests use the selected store as the X-Store-ID
              header.
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchSourceFiles()}
            disabled={!selectedStoreId || loading}
            className="inline-flex items-center justify-center rounded-lg bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {loading ? 'Loading...' : 'Reload'}
          </button>
        </div>
      </ComponentCard>

      <ComponentCard
        title="Knowledge Base Dashboard"
        className="overflow-hidden"
      >
        {!selectedStoreId ? (
          <div className="rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-400">
            Select a store before loading the knowledge base dashboard.
          </div>
        ) : loadingDashboard && !dashboardSummary ? (
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
            Loading knowledge base dashboard...
          </div>
        ) : (
          <div className="space-y-5">
            {dashboardError ? (
              <div className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-500 dark:border-error-800 dark:bg-error-900/20">
                {dashboardError}
              </div>
            ) : null}

            {dashboardSummary?.compiler.enabled === false ? (
              <div className="rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-400">
                Knowledge base compilation is currently disabled for this
                environment.
              </div>
            ) : null}

            {dashboardSummary && dashboardSummary.compiler.enabled !== false ? (
              <>
                <div className="grid gap-4 md:grid-cols-4">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Ready to compile
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                      {dashboardSummary.source_files.eligible_count}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      files ready
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Total sources
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                      {dashboardSummary.source_files.total}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {dashboardSummary.source_files.archived_count} archived
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Latest draft
                    </p>
                    <p className="mt-1 text-sm">
                      {renderArtifactLink(
                        dashboardSummary.latest_draft_artifact,
                        'Draft',
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Approved artifact
                    </p>
                    <p className="mt-1 text-sm">
                      {renderArtifactLink(
                        dashboardSummary.latest_approved_artifact,
                        'Approved',
                      )}
                    </p>
                  </div>
                </div>

                {Object.keys(
                  dashboardSummary.source_files.breakdown_by_category,
                ).length ? (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(
                      dashboardSummary.source_files.breakdown_by_category,
                    ).map(([category, count]) => (
                      <span
                        key={category}
                        className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                      >
                        {labelFor(
                          categoryOptions,
                          category as KnowledgeCategory,
                        )}
                        : {count}
                      </span>
                    ))}
                  </div>
                ) : null}

                {dashboardSummary.active_job ? (
                  <div className="rounded-2xl border border-brand-200 bg-brand-50 p-5 dark:border-brand-500/30 dark:bg-brand-500/10">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-brand-700 dark:text-brand-300">
                          Compiling knowledge base...
                        </p>
                        <p className="mt-1 text-sm text-brand-700/80 dark:text-brand-300/80">
                          {
                            compilationStageLabels[
                              dashboardSummary.active_job.status
                            ]
                          }{' '}
                          with{' '}
                          {dashboardSummary.active_job.source_file_ids.length}{' '}
                          source files
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-brand-700 ring-1 ring-brand-200 dark:bg-gray-900 dark:text-brand-300 dark:ring-brand-500/30">
                        <span className="h-3 w-3 animate-spin rounded-full border-2 border-brand-200 border-t-brand-500" />
                        Job #{dashboardSummary.active_job.id}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 p-5 sm:flex-row sm:items-center sm:justify-between dark:border-gray-800">
                    <div>
                      <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
                        {dashboardSummary.source_files.eligible_count} files
                        ready to compile
                      </p>
                      {!dashboardSummary.can_compile_now ? (
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {dashboardSummary.blocked_reasons.join(' · ') ||
                            'Compilation is not available right now.'}
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={startDashboardCompile}
                      disabled={
                        compileLoading || !dashboardSummary.can_compile_now
                      }
                      title={
                        dashboardSummary.can_compile_now
                          ? undefined
                          : dashboardSummary.blocked_reasons.join(' · ')
                      }
                      className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-theme-xs transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {compileLoading
                        ? 'Starting...'
                        : 'Compile Knowledge Base'}
                    </button>
                  </div>
                )}

                <div className="grid gap-4 xl:grid-cols-2">
                  {renderDashboardBucket(
                    'Pending extraction',
                    dashboardSummary.source_files.pending_extraction,
                    'pending',
                  )}
                  {renderDashboardBucket(
                    'Extraction failed',
                    dashboardSummary.source_files.extraction_failed,
                    'failed',
                    'Reprocess these files before compiling.',
                  )}
                  {renderDashboardBucket(
                    'Skipped files',
                    dashboardSummary.source_files.skipped,
                    'skipped',
                    'Image-only files are skipped until OCR is supported.',
                  )}
                  {renderDashboardBucket(
                    'Excluded by admin',
                    dashboardSummary.source_files.excluded_by_admin,
                    'excluded',
                  )}
                </div>
              </>
            ) : null}
          </div>
        )}
      </ComponentCard>

      <ComponentCard
        title={`Knowledge Sources for ${selectedStoreLabel || 'selected store'}`}
        className="overflow-visible"
      >
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-3xl text-sm text-gray-500 dark:text-gray-400">
            Upload and manage restaurant source documents that feed future voice
            agent knowledge compilation.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openWebsiteImport}
              disabled={!selectedStoreId}
              className="inline-flex items-center justify-center rounded-lg border border-brand-500 px-5 py-3 text-sm font-semibold text-brand-600 transition hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-brand-400"
            >
              Import website
            </button>
            <button
              type="button"
              onClick={() => {
                resetUploadForm();
                setIsUploadOpen(true);
              }}
              disabled={!selectedStoreId}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-theme-xs transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Upload file
            </button>
          </div>
        </div>

        {!selectedStoreId ? (
          <div className="mb-4 rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-400">
            Select a store before loading knowledge sources.
          </div>
        ) : null}

        {error ? (
          <div className="mb-4 rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-500 dark:border-error-800 dark:bg-error-900/20">
            {error}
          </div>
        ) : null}

        {success ? (
          <div className="mb-4 rounded-lg border border-success-200 bg-success-50 p-4 text-sm text-success-600 dark:border-success-800 dark:bg-success-900/20 dark:text-success-400">
            {success}
          </div>
        ) : null}

        <div className="mb-5 grid gap-4 lg:grid-cols-[minmax(220px,1.4fr)_repeat(3,minmax(150px,1fr))]">
          <div>
            <Label>Search</Label>
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search filename, category, or status"
              disabled={!selectedStoreId}
            />
          </div>
          <div>
            <Label>Category</Label>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
              disabled={!selectedStoreId}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            >
              <option value="">All categories</option>
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Scope</Label>
            <select
              value={scopeFilter}
              onChange={(e) => {
                setScopeFilter(e.target.value);
                setPage(1);
              }}
              disabled={!selectedStoreId}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            >
              <option value="">All scopes</option>
              {scopeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Status</Label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              disabled={!selectedStoreId}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            >
              <option value="">All statuses</option>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-5 flex flex-wrap gap-3">
          <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                setPage(1);
              }}
              className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
            />
            Show archived
          </label>
          <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={onlyIncluded}
              onChange={(e) => {
                setOnlyIncluded(e.target.checked);
                setPage(1);
              }}
              className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
            />
            Only included in compilation
          </label>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
            Loading knowledge sources...
          </div>
        ) : sourceFiles.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 p-8 text-center dark:border-gray-700">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              No knowledge sources uploaded yet.
            </p>
            <div className="mt-4 flex justify-center gap-2">
              <button
                type="button"
                onClick={openWebsiteImport}
                disabled={!selectedStoreId}
                className="inline-flex items-center justify-center rounded-lg border border-brand-500 px-5 py-3 text-sm font-semibold text-brand-600 disabled:opacity-50"
              >
                Import website
              </button>
              <button
                type="button"
                onClick={() => {
                  resetUploadForm();
                  setIsUploadOpen(true);
                }}
                disabled={!selectedStoreId}
                className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-theme-xs transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Upload file
              </button>
            </div>
          </div>
        ) : (
          <div className="relative overflow-visible rounded-[24px] border border-[rgb(var(--app-border)/0.72)] bg-[rgb(var(--app-surface)/0.74)]">
            <div className="max-w-full overflow-x-auto">
              <table className="min-w-[1180px] w-full text-left text-sm">
                <thead className="border-b border-[rgb(var(--app-border)/0.72)] bg-[rgb(var(--app-surface-muted)/0.72)] text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <tr>
                    {SOURCE_COLUMNS.map((column) => (
                      <th key={column.key} className="px-4 py-3">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 uppercase"
                          aria-label={`Sort by ${column.label}`}
                          onClick={() => {
                            setSourceSort((current) =>
                              toggleSortState(current, column.key),
                            );
                            setPage(1);
                          }}
                        >
                          {column.label}
                          {sourceSort.key === column.key
                            ? sourceSort.direction === 'asc'
                              ? ' ↑'
                              : ' ↓'
                            : ''}
                        </button>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {sourceFiles.map((file) => (
                    <tr
                      key={file.id}
                      className="align-top hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={() => {
                            setOpenActionMenuId(null);
                            previewFile(file);
                          }}
                          className="text-left font-semibold text-brand-600 underline-offset-2 hover:underline dark:text-brand-400"
                        >
                          {file.original_filename}
                        </button>
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {file.source_kind === 'website'
                            ? 'Website'
                            : 'Upload'}{' '}
                          · {file.mime_type}
                        </div>
                        {file.source_kind === 'website' &&
                        file.canonical_url ? (
                          <a
                            href={file.canonical_url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 block max-w-xs truncate text-xs text-brand-600 hover:underline dark:text-brand-400"
                          >
                            {file.canonical_url}
                          </a>
                        ) : null}
                        {file.token_count != null &&
                        file.token_count > 50_000 ? (
                          <p className="mt-1 text-xs font-semibold text-warning-700 dark:text-warning-400">
                            {file.token_count.toLocaleString()} tokens exceeds
                            the 50k per-file compile limit
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          data-category={file.category}
                          title={file.category}
                          className="text-gray-700 dark:text-gray-300"
                        >
                          {labelFor(categoryOptions, file.category)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-700 dark:text-gray-300">
                        {labelFor(scopeOptions, file.scope)}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusBadgeClassName(
                            file.status,
                          )}`}
                        >
                          {statusLabel(file.status)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-700 dark:text-gray-300">
                        {file.include_in_compilation ? 'Yes' : '-'}
                      </td>
                      <td className="px-4 py-4">
                        {renderExtractionBadge(file)}
                      </td>
                      <td className="px-4 py-4 text-gray-700 dark:text-gray-300">
                        {formatBytes(file.size_bytes)}
                      </td>
                      <td className="px-4 py-4 text-gray-700 dark:text-gray-300">
                        {formatRelativeTime(file.created_at)}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div
                          className="relative inline-block"
                          data-knowledge-action-menu
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenActionMenuId(
                                openActionMenuId === file.id ? null : file.id,
                              );
                            }}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                            aria-label={`Actions for ${file.original_filename}`}
                          >
                            <svg
                              className="h-5 w-5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <circle cx="10" cy="4" r="1.5" />
                              <circle cx="10" cy="10" r="1.5" />
                              <circle cx="10" cy="16" r="1.5" />
                            </svg>
                          </button>
                          {openActionMenuId === file.id ? (
                            <div
                              className="absolute right-0 z-30 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 text-left shadow-lg dark:border-gray-700 dark:bg-gray-800"
                              data-knowledge-action-menu
                            >
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                                onClick={() => {
                                  setOpenActionMenuId(null);
                                  // Open immediately with the table row, then refresh detail.
                                  setSelectedFile(file);
                                  setDetailForm({
                                    category: file.category,
                                    scope: file.scope,
                                    include_in_compilation:
                                      file.include_in_compilation,
                                  });
                                  fetchDetail(file.id);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                disabled={actionLoadingId === file.id}
                                className="w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent dark:text-gray-300 dark:hover:bg-gray-700 dark:disabled:text-gray-500"
                                onClick={() => {
                                  setOpenActionMenuId(null);
                                  previewFile(file);
                                }}
                              >
                                {actionLoadingId === file.id
                                  ? 'Loading...'
                                  : 'Preview'}
                              </button>
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                                onClick={() => {
                                  setOpenActionMenuId(null);
                                  downloadFile(file);
                                }}
                              >
                                Download
                              </button>
                              <button
                                type="button"
                                disabled={
                                  file.status === 'archived' ||
                                  actionLoadingId === file.id ||
                                  !!activeRecrawls[file.id]?.cancel_requested_at
                                }
                                className="w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent dark:text-gray-300 dark:hover:bg-gray-700 dark:disabled:text-gray-500"
                                onClick={() => {
                                  setOpenActionMenuId(null);
                                  if (activeRecrawls[file.id]) {
                                    cancelRecrawl(file);
                                  } else if (file.source_kind === 'website') {
                                    recrawlFile(file);
                                  } else {
                                    reprocessFile(file);
                                  }
                                }}
                              >
                                {activeRecrawls[file.id]?.cancel_requested_at
                                  ? 'Cancellation requested'
                                  : activeRecrawls[file.id]
                                    ? 'Cancel recrawl'
                                    : file.source_kind === 'website'
                                      ? 'Recrawl'
                                      : 'Reprocess'}
                              </button>
                              <button
                                type="button"
                                disabled={file.status === 'archived'}
                                className="w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent dark:text-gray-300 dark:hover:bg-gray-700 dark:disabled:text-gray-500"
                                onClick={() => {
                                  setOpenActionMenuId(null);
                                  setConfirmAction({ type: 'archive', file });
                                }}
                              >
                                Archive
                              </button>
                              {file.status === 'archived' ? (
                                <button
                                  type="button"
                                  disabled={actionLoadingId === file.id}
                                  className="w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent dark:text-gray-300 dark:hover:bg-gray-700 dark:disabled:text-gray-500"
                                  onClick={() => {
                                    setOpenActionMenuId(null);
                                    unarchiveFile(file);
                                  }}
                                >
                                  {actionLoadingId === file.id
                                    ? 'Working...'
                                    : 'Unarchive'}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="w-full px-4 py-2 text-left text-xs text-error-600 hover:bg-error-50 dark:text-error-300 dark:hover:bg-error-900/30"
                                onClick={() => {
                                  setOpenActionMenuId(null);
                                  setConfirmAction({ type: 'delete', file });
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-5 flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between dark:border-gray-800">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages} ({totalCount} files)
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              disabled={page <= 1 || loading}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() =>
                setPage((value) => Math.min(totalPages, value + 1))
              }
              disabled={page >= totalPages || loading}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Next
            </button>
          </div>
        </div>
      </ComponentCard>

      <WebsiteKnowledgeImportModal
        isOpen={isWebsiteImportOpen}
        storeId={selectedStoreId}
        openIntent={websiteImportIntent}
        pollController={pollController}
        onClose={() => setIsWebsiteImportOpen(false)}
        onImportProgress={async () => {
          await Promise.all([
            fetchSourceFiles({ silent: true }),
            fetchDashboardSummary({ silent: true }),
          ]);
        }}
        onImportFinished={async (job) => {
          await Promise.all([
            fetchSourceFiles({ silent: true }),
            fetchDashboardSummary({ silent: true }),
          ]);
          if (job.status === 'completed_with_errors') {
            setSuccess(
              `Website import completed with ${job.failed_count || 0} failed resource${job.failed_count === 1 ? '' : 's'}.`,
            );
          } else if (job.status === 'completed') {
            setSuccess(
              `Imported ${job.completed_count || job.items?.length || 0} website resource${job.completed_count === 1 ? '' : 's'}.`,
            );
          }
        }}
      />

      <Modal
        isOpen={isUploadOpen}
        onClose={() => {
          if (!uploading) setIsUploadOpen(false);
        }}
        className="max-w-2xl p-6 lg:p-8"
      >
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Upload Knowledge Sources
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {ACCEPTED_FILE_COPY}
            </p>
          </div>

          <div>
            <Label>Source files</Label>
            <input
              type="file"
              multiple
              accept={ACCEPTED_FILE_TYPES}
              disabled={uploading}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setUploadForm((prev) => ({ ...prev, files }));
                setUploadError(validateUploadFilesBasic(files));
                if (files.length)
                  void validateUploadFiles(files).then(setUploadError);
              }}
              className="block w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs file:mr-4 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-gray-700 focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:file:bg-gray-800 dark:file:text-gray-200"
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              PNG and JPEG uploads are accepted now and will show extraction as
              Skipped until OCR support is added. You can select multiple files;
              each file uploads as a separate source.
            </p>
            {uploadForm.files.length ? (
              <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/50">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Selected files ({uploadForm.files.length})
                </p>
                <div className="mt-2 max-h-32 space-y-1 overflow-auto">
                  {uploadForm.files.map((file) => (
                    <div
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      className="flex items-center justify-between gap-3 text-xs text-gray-700 dark:text-gray-300"
                    >
                      <span className="truncate">{file.name}</span>
                      <span className="shrink-0 text-gray-500 dark:text-gray-400">
                        {formatBytes(file.size)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <Label>Category</Label>
              <select
                value={uploadForm.category}
                onChange={(e) =>
                  setUploadForm((prev) => ({
                    ...prev,
                    category: e.target.value as KnowledgeCategory,
                  }))
                }
                disabled={uploading}
                className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
              >
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Scope</Label>
              <select
                value={uploadForm.scope}
                onChange={(e) =>
                  setUploadForm((prev) => ({
                    ...prev,
                    scope: e.target.value as KnowledgeScope,
                  }))
                }
                disabled={uploading}
                className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
              >
                {scopeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={uploadForm.include_in_compilation}
              onChange={(e) =>
                setUploadForm((prev) => ({
                  ...prev,
                  include_in_compilation: e.target.checked,
                }))
              }
              disabled={uploading}
              className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
            />
            Include in KB compilation
          </label>

          {uploading ? (
            <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
              <div className="h-full w-2/3 animate-pulse rounded-full bg-brand-500" />
            </div>
          ) : null}

          {uploadError ? (
            <div className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-500 dark:border-error-800 dark:bg-error-900/20">
              {uploadError}
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setIsUploadOpen(false)}
              disabled={uploading}
              className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={
                uploading || !!validateUploadFilesBasic(uploadForm.files)
              }
              className="rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {uploading
                ? 'Uploading...'
                : uploadForm.files.length > 1
                  ? `Upload ${uploadForm.files.length} files`
                  : 'Upload file'}
            </button>
          </div>
        </div>
      </Modal>

      {selectedFile ? (
        <div className="fixed inset-0 z-[99999] flex justify-end">
          <div
            className="fixed inset-0 bg-gray-400/50 backdrop-blur-sm"
            onClick={() => {
              setSelectedFile(null);
              setDetailForm(null);
            }}
          />
          <div className="relative w-full max-w-2xl animate-slide-in-right overflow-y-auto bg-white shadow-2xl dark:bg-gray-900">
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-200 bg-white/90 px-6 py-4 backdrop-blur dark:border-gray-700 dark:bg-gray-900/90">
              <div>
                <h2 className="break-words text-lg font-semibold text-gray-900 dark:text-white">
                  {selectedFile.original_filename}
                </h2>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {labelFor(categoryOptions, selectedFile.category)}
                  </span>
                  <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {labelFor(scopeOptions, selectedFile.scope)}
                  </span>
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusBadgeClassName(
                      selectedFile.status,
                    )}`}
                  >
                    {statusLabel(selectedFile.status)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedFile(null);
                  setDetailForm(null);
                }}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                aria-label="Close detail drawer"
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            </div>

            {loadingDetail ? (
              <div className="flex items-center justify-center py-20 text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            ) : (
              <div className="space-y-6 p-6">
                {detailError ? (
                  <div className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-500 dark:border-error-800 dark:bg-error-900/20">
                    {detailError}
                  </div>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-2">
                  <DetailField
                    label="Uploaded by"
                    value={selectedFile.uploaded_by || '-'}
                  />
                  <DetailField
                    label="Uploaded at"
                    value={formatDateTime(selectedFile.created_at)}
                  />
                  <DetailField
                    label="Size"
                    value={formatBytes(selectedFile.size_bytes)}
                  />
                  <DetailField label="MIME" value={selectedFile.mime_type} />
                  <DetailField
                    label="Extraction status"
                    value={renderExtractionBadge(selectedFile, true)}
                  />
                  <DetailField
                    label="Last extracted"
                    value={formatDateTime(selectedFile.extracted_at)}
                  />
                  <DetailField
                    label="Extraction attempts"
                    value={String(selectedFile.extraction_attempts)}
                  />
                  <DetailField
                    label="Has extracted text"
                    value={selectedFile.has_extracted_text ? 'Yes' : 'No'}
                  />
                  <div className="sm:col-span-2">
                    <DetailField
                      label="Checksum"
                      mono
                      value={
                        <button
                          type="button"
                          title={selectedFile.checksum}
                          onClick={() => {
                            navigator.clipboard?.writeText(
                              selectedFile.checksum,
                            );
                            setSuccess('Checksum copied.');
                          }}
                          className="text-left underline-offset-2 hover:underline"
                        >
                          {truncateChecksum(selectedFile.checksum)}
                        </button>
                      }
                    />
                  </div>
                  {selectedFile.extraction_error ? (
                    <div className="sm:col-span-2">
                      <DetailField
                        label="Extraction error"
                        value={selectedFile.extraction_error}
                      />
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-3 border-y border-gray-200 py-5 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => previewFile(selectedFile)}
                    disabled={actionLoadingId === selectedFile.id}
                    className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    {actionLoadingId === selectedFile.id
                      ? 'Loading...'
                      : 'Preview'}
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadFile(selectedFile)}
                    disabled={actionLoadingId === selectedFile.id}
                    className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    Download
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      activeRecrawls[selectedFile.id]
                        ? cancelRecrawl(selectedFile)
                        : selectedFile.source_kind === 'website'
                          ? recrawlFile(selectedFile)
                          : reprocessFile(selectedFile)
                    }
                    disabled={
                      selectedFile.status === 'archived' ||
                      actionLoadingId === selectedFile.id ||
                      !!activeRecrawls[selectedFile.id]?.cancel_requested_at
                    }
                    className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    {activeRecrawls[selectedFile.id]?.cancel_requested_at
                      ? 'Cancellation requested'
                      : activeRecrawls[selectedFile.id]
                        ? 'Cancel recrawl'
                        : selectedFile.source_kind === 'website'
                          ? 'Recrawl'
                          : 'Reprocess'}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmAction({ type: 'archive', file: selectedFile })
                    }
                    disabled={selectedFile.status === 'archived'}
                    className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    Archive
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmAction({ type: 'delete', file: selectedFile })
                    }
                    className="rounded-lg bg-error-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-error-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>

                {detailForm ? (
                  <div className="space-y-5">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                      Editable metadata
                    </h3>
                    <div className="grid gap-5 md:grid-cols-2">
                      <div>
                        <Label>Category</Label>
                        <select
                          value={detailForm.category}
                          onChange={(e) =>
                            setDetailForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    category: e.target
                                      .value as KnowledgeCategory,
                                  }
                                : prev,
                            )
                          }
                          disabled={savingDetail}
                          className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
                        >
                          {categoryOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Scope</Label>
                        <select
                          value={detailForm.scope}
                          onChange={(e) =>
                            setDetailForm((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    scope: e.target.value as KnowledgeScope,
                                  }
                                : prev,
                            )
                          }
                          disabled={savingDetail}
                          className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
                        >
                          {scopeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={detailForm.include_in_compilation}
                        onChange={(e) =>
                          setDetailForm((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  include_in_compilation: e.target.checked,
                                }
                              : prev,
                          )
                        }
                        disabled={savingDetail}
                        className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
                      />
                      Include in compilation
                    </label>
                    <button
                      type="button"
                      onClick={saveDetail}
                      disabled={!detailDirty || savingDetail}
                      className="rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {savingDetail ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      ) : null}

      <Modal
        isOpen={!!preview}
        onClose={() => {
          setPreview(null);
          setPreviewError('');
        }}
        className="max-w-6xl p-6 lg:p-8"
      >
        {preview ? (
          <div className="space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="break-words text-lg font-semibold text-gray-900 dark:text-white">
                  {preview.file.original_filename}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {preview.mimeType || preview.file.mime_type} ·{' '}
                  {formatBytes(preview.file.size_bytes)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPreview(null);
                  setPreviewError('');
                }}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                aria-label="Close preview"
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            </div>

            {previewError ? (
              <div className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-500 dark:border-error-800 dark:bg-error-900/20">
                {previewError}
              </div>
            ) : null}

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/40">
              {isImagePreviewType(preview.mimeType) ? (
                <div className="flex max-h-[70vh] items-center justify-center overflow-auto p-4">
                  <img
                    src={preview.objectUrl}
                    alt={preview.file.original_filename}
                    className="max-h-[65vh] max-w-full rounded-lg object-contain"
                  />
                </div>
              ) : isPdfPreviewType(
                  preview.mimeType,
                  preview.file.original_filename,
                ) ? (
                <iframe
                  title={preview.file.original_filename}
                  src={preview.objectUrl}
                  className="h-[70vh] w-full bg-white"
                />
              ) : preview.textContent != null ? (
                <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words p-5 text-sm leading-6 text-gray-800 dark:text-gray-100">
                  {preview.textContent || 'This file is empty.'}
                </pre>
              ) : (
                <div className="p-6 text-sm text-gray-600 dark:text-gray-300">
                  Preview is not available for this file type. You can download
                  the file to view it locally.
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setPreview(null);
                  setPreviewError('');
                }}
                className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Close
              </button>
              <button
                type="button"
                onClick={downloadPreview}
                className="rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600"
              >
                Download
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={!!confirmAction}
        onClose={() => {
          if (!actionLoadingId) setConfirmAction(null);
        }}
        className="max-w-lg p-6 lg:p-8"
      >
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {confirmAction?.type === 'delete'
                ? 'Delete Knowledge Source'
                : 'Archive Knowledge Source'}
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {confirmAction?.type === 'delete'
                ? 'Delete is permanent and removes the stored original file.'
                : 'Archived files are excluded from compilation and hidden by default.'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm font-medium text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90">
            {confirmAction?.file.original_filename}
          </div>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setConfirmAction(null)}
              disabled={!!actionLoadingId}
              className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!confirmAction || !!actionLoadingId}
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === 'delete') {
                  deleteFile(confirmAction.file);
                } else {
                  archiveFile(confirmAction.file);
                }
              }}
              className={`rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${
                confirmAction?.type === 'delete'
                  ? 'bg-error-500 hover:bg-error-600'
                  : 'bg-brand-500 hover:bg-brand-600'
              }`}
            >
              {actionLoadingId
                ? 'Working...'
                : confirmAction?.type === 'delete'
                  ? 'Delete'
                  : 'Archive'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default KnowledgeBasesTab;
